import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, ModalController } from '@ionic/angular';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';

import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  selector: 'app-upsert-survey-modal',
  templateUrl: './upsert-survey-modal.component.html',
  styleUrls: ['./upsert-survey-modal.component.scss'],
})
export class UpsertSurveyModalComponent implements OnInit {
  @Input() survey?: any;
  @Input() shouldSave = true;

  isLoading = false;
  ColumnMode = ColumnMode;
  routerSub: Subscription;
  isAnalyticsModalOpen = false;
  isCreateSurveyModalOpen = false;
  currentSurveyId?: string;
  selectedProjectId: string | null = null;
  selectedProjectName: string = null;
  emailNotificationUserIds: string[] = [];
  notificationUsers: any[] = [];

  constructor(
    private apiService: ApiService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    public authService: AuthService,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  ngOnInit() {
    if (!this.survey) {
      this.survey = { style: {} };
    }
    if (!this.survey.style) {
      this.survey.style = {};
    }

    if (this.survey?.id) {
      this.isLoading = true;

      this.apiService.getPromise(`/surveys/${this.survey.id}`).then((survey: any) => {
        this.survey = survey;
        this.emailNotificationUserIds = survey.emailNotificationUserIds || [];
        this.loadNotificationUsers();
        this.isLoading = false;
      });
    } else {
      this.survey = {
        type: 'csat',
        style: {
          buttonLayout: 'inline',
          postSubmissionMessage: 'Thanks for letting us know!',
          selectionOptions: 'emojiFaces',
          buttonLabel: 'Fill out survey',
        },
        projectId: this.authService.selectedProject?.id ?? null,
      };
      this.emailNotificationUserIds = [this.authService.userId];
      this.notificationUsers = [this.authService.profile];
    }
  }

  selectProject(event: any) {
    this.searchablePopoverService.projects({
      callback: (id, name, project) => {
        this.selectedProjectId = id;
        this.selectedProjectName = name;
      },
      event,
      showNoneOption: true,
      noneOptionLabel: 'All projects',
      ownerOnly: true,
    });
  }

  async loadNotificationUsers() {
    if (this.emailNotificationUserIds.length === 0) {
      this.notificationUsers = [];
      return;
    }

    try {
      const users = await Promise.all(
        this.emailNotificationUserIds.map((id) => this.apiService.getPromise(`/users/${id}`)),
      );
      this.notificationUsers = users.filter(Boolean);
    } catch (error) {
      console.error('Failed to load notification users:', error);
      this.notificationUsers = [];
    }
  }

  addNotificationUser(event: any) {
    this.searchablePopoverService.users({
      callback: (id: string, name: string, user: any) => {
        if (id && !this.emailNotificationUserIds.includes(id)) {
          this.emailNotificationUserIds.push(id);
          this.notificationUsers.push(user);
        }
      },
      event,
      showNoneOption: false,
      includeExternalUsers: false,
      includeEmailOnlyUsers: false,
    });
  }

  removeNotificationUser(userId: string) {
    this.emailNotificationUserIds = this.emailNotificationUserIds.filter((id) => id !== userId);
    this.notificationUsers = this.notificationUsers.filter((user) => user.id !== userId);
  }

  dismiss(data?: any) {
    this.modalCtrl.dismiss(data);
  }

  async upsertSurvey() {
    this.isLoading = true;

    try {
      if (this.shouldSave) {
        if (!this.survey.id) {
          const projectId = this.authService.selectedProject
            ? this.authService.selectedProject.id
            : this.selectedProjectId;

          this.survey.projectId = projectId;
        }

        // Include notification user IDs in the request
        const surveyData = {
          ...this.survey,
          emailNotificationUserIds: this.emailNotificationUserIds,
        };

        if (this.survey.id) {
          await this.apiService.patchPromise(`/surveys/${this.survey.id}`, surveyData);
        } else {
          await this.apiService.postPromise(`/surveys`, surveyData);
        }
      }

      this.dismiss(this.survey);
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  async deleteSurvey(surveyId: string) {
    const alert = await this.alertCtrl.create({
      header: 'Delete survey',
      subHeader: 'Are you sure you want to delete this survey?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes',
          handler: async () => {
            try {
              await this.apiService.deletePromise(`/surveys/${surveyId}`);

              this.dismiss();
            } catch (err) {
              this.msgSrvc.showError(err);
            } finally {
              this.isLoading = false;
            }
          },
        },
      ],
    });

    await alert.present();
  }
}
